import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-apollo-hooks'
import _ from 'lodash';

import { TourCategoriesQuery, ITourCategoriesQueryVariables, ITourCategoriesQueryData, withAppStoreValue, AppStoreDispatchAction, EAppActionTypes } from '../store'
import { Checkbox } from './checkbox';

interface ICategoryProps {
    id: number
    title: string
    total: number
    onChange: (id: number, checked: boolean) => void
    checked: boolean
}

const Category: React.FC<ICategoryProps> = (props) => {
    const { id, title, total, onChange, checked } = props
    return (
        <li>
            <Checkbox
                label={`${title} ${total}`}
                onChange={(e) => onChange(id, e.target.checked)}
                checked={checked}
            />
        </li>
    )
}

interface ITopCategoryExtraProps {
    title: string
    slug: string
    total: number
    categories: Array<any>
}

interface ITopCategoryAppStoreProps {
    dispatch: AppStoreDispatchAction
    selectedCategories: number[]
}

const TopCategoryCore: React.FC<ITopCategoryExtraProps & ITopCategoryAppStoreProps> = ({ title, slug, total, categories, selectedCategories, dispatch }) => {
    const setSelectedCategories = useCallback((selectedCategories: number[]) => {
        dispatch({ type: EAppActionTypes.SetSelectedCategories, selectedCategories})
    }, [])
    const selectAll = (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (e) e.preventDefault();
        setSelectedCategories(_.union(selectedCategories, categories.map(({ id }) => id)))
    }
    const deselectAll = (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (e) e.preventDefault();
        setSelectedCategories(_.without(selectedCategories, ...(categories.map(({ id }) => id))))
    }
    const toggleSelected = (id: number, checked: boolean) => {
        if (checked) setSelectedCategories([...selectedCategories, id])
        else setSelectedCategories(selectedCategories.filter(item => item !== id))
    }

    const { t } = useTranslation();
    return (
        <li>
            <div id="item_0" className="cat-item">
                <div className="icon" onClick={() => {
                    if (selectedCategories.length === categories.length) {
                        deselectAll()
                    } else {
                        selectAll()
                    }
                }}>
                    <span className={"icon-" + slug} style={{ lineHeight: 2 }} />
                </div>
                <div className="text">
                    <span className="cat-title">{title}
                        <span className="post-number text-dark-blue">{total}</span>
                    </span>
                    <div className="sub-cat">
                        <p className="text-left m-13"><a href="/" onClick={selectAll}>{t('Select All')}</a> | <a href="/" onClick={deselectAll}>{t('Deselect All')}</a></p>
                        <ul className="medium-block-grid-2 small-block-grid-1">
                            {categories.map(item =>
                                <Category
                                    key={item.id}
                                    {...item}
                                    checked={selectedCategories.findIndex(cat => cat === item.id) !== -1}
                                    onChange={toggleSelected}
                                />
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </li>
    )
}

const TopCategory = withAppStoreValue<ITopCategoryAppStoreProps, ITopCategoryExtraProps>(TopCategoryCore, ['dispatch', 'categories.selectedCategories'])

interface ICategoriesFilterExtraProps {
    filterShow: boolean
    countryId: number
    applyFilter: () => void
    resetFilter: () => void
}

interface ICategoriesFilterAppStoreProps {
    dispatch: AppStoreDispatchAction
    selectedCategories: number[]
}

const CategoriesFilterCore: React.FC<ICategoriesFilterExtraProps & ICategoriesFilterAppStoreProps> = memo(({ filterShow, countryId, selectedCategories, dispatch, applyFilter, resetFilter }) => {
    const { t, i18n: { languages: [language] } } = useTranslation()

    const handleApplyFilter = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault()
        dispatch({ type: EAppActionTypes.SetFilter, filter: { categories: selectedCategories, hasCoupons: false }})
        applyFilter()
    }
    const handleResetFilter = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault()
        dispatch({ type: EAppActionTypes.SetFilter, filter: { categories: [], hasCoupons: false }})
        dispatch({ type: EAppActionTypes.SetSelectedCategories, selectedCategories: []})
        resetFilter()
    }

    const { data } = useQuery<any, ITourCategoriesQueryVariables, any>(TourCategoriesQuery, {
        variables: {
            where: {
                language,
                countryId
            }
        }
    });

    const tourCategories = data && (data as ITourCategoriesQueryData).tourCategories;

    return (
        <div className={filterShow ? 'tour-cat' : ''}>
            {filterShow &&
                <div className="listing-cat-warapper">
                    <div className="tour-cat-header p-b15">
                        <h4 className="m-t0">{t('Categories')}
                            <span className="fr filter-section">
                                <a href="/" onClick={handleApplyFilter} className="cta cta-small">{t('Apply Filter')}</a>
                                <a href="/" onClick={handleResetFilter} className="cta-small cta cta-back">{t('Reset Filter')}</a>
                            </span>
                        </h4>
                    </div>
                    <ul className="medium-block-grid-1 large-block-grid-1 small-block-grid-1 list-categories">
                        {tourCategories && tourCategories.map(item =>
                            <TopCategory
                                key={item.id}
                                {...item}
                            />
                        )}
                    </ul>
                </div>
            }
        </div>
    );
})

export const CategoriesFilter = withAppStoreValue<ICategoriesFilterAppStoreProps, ICategoriesFilterExtraProps>(CategoriesFilterCore, ['dispatch', 'categories.selectedCategories'])